// Enhanced TeamContext.tsx with better debugging and error handling
// src/contexts/TeamContext.tsx

import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { getUserTeams, getPersonalTeam, type TeamWithMembers } from '../lib/teams';

interface TeamContextType {
  currentTeam: TeamWithMembers | null;
  userTeams: TeamWithMembers[];
  switchTeam: (teamId: string) => Promise<void>;
  refreshTeams: () => Promise<void>;
  loading: boolean;
  error: string | null;
}

const TeamContext = createContext<TeamContextType | undefined>(undefined);

interface TeamProviderProps {
  children: ReactNode;
}

export function TeamProvider({ children }: TeamProviderProps) {
  const [currentTeam, setCurrentTeam] = useState<TeamWithMembers | null>(null);
  const [userTeams, setUserTeams] = useState<TeamWithMembers[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    loadTeams();
    
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const loadTeams = async (retryCount = 0, maxRetries = 3) => {
    try {
      if (!mountedRef.current) return;
      
      setLoading(true);
      setError(null);
      
      const teams = await getUserTeams();
      
      // Don't check mounted state here - we want to complete the operation if teams loaded successfully
      setUserTeams(teams);
      
      if (teams.length === 0) {
        setCurrentTeam(null);
        return;
      }
      
      // Set current team logic
      const savedTeamId = localStorage.getItem('currentTeamId');
      let teamToSet: TeamWithMembers | null = null;
      
      // Try to restore saved team
      if (savedTeamId) {
        teamToSet = teams.find(team => team.id === savedTeamId) || null;
      }
      
      // Fall back to personal team or first team
      if (!teamToSet) {
        teamToSet = getPersonalTeam(teams) || teams[0];
      }
      
      if (teamToSet) {
        setCurrentTeam(teamToSet);
        localStorage.setItem('currentTeamId', teamToSet.id);
      }
    } catch (err) {
      if (!mountedRef.current) return;
      
      // Retry logic for transient errors
      if (retryCount < maxRetries && shouldRetry(err)) {
        timeoutRef.current = setTimeout(() => {
          if (mountedRef.current) {
            loadTeams(retryCount + 1, maxRetries);
          }
        }, (retryCount + 1) * 1000); // Exponential backoff: 1s, 2s, 3s
        return;
      }
      
      // Set error message after all retries failed
      let errorMessage = 'Failed to load teams';
      
      if (err instanceof Error) {
        // Provide more specific error messages
        if (err.message.includes('Teams feature not yet configured')) {
          errorMessage = 'Teams feature not yet configured. Please run database migrations.';
        } else if (err.message.includes('relation') && err.message.includes('does not exist')) {
          errorMessage = 'Team tables not found. Please contact your administrator to run database migrations.';
        } else if (err.message.includes('Failed to fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      console.error('Failed to load teams:', err);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to determine if we should retry
  const shouldRetry = (error: any): boolean => {
    if (!error) return false;
    
    const errorMessage = error.message?.toLowerCase() || '';
    
    // Retry on these types of errors
    return (
      errorMessage.includes('network') ||
      errorMessage.includes('fetch') ||
      errorMessage.includes('timeout') ||
      errorMessage.includes('connection') ||
      errorMessage.includes('auth session missing') ||
      // Don't retry on permanent errors
      (!errorMessage.includes('not found') && 
       !errorMessage.includes('does not exist') && 
       !errorMessage.includes('permission denied'))
    );
  };

  const switchTeam = async (teamId: string) => {
    try {
      setError(null);
      
      const team = userTeams.find(t => t.id === teamId);
      if (!team) {
        throw new Error('Team not found');
      }
      
      setCurrentTeam(team);
      localStorage.setItem('currentTeamId', teamId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to switch team';
      setError(errorMessage);
    }
  };

  const refreshTeams = async () => {
    try {
      if (!mountedRef.current) return;
      
      // Clear any existing errors
      setError(null);
      
      // Add a longer delay to allow database changes to propagate
      await new Promise(resolve => setTimeout(resolve, 1500)); // Increased to 1.5 seconds
      
      if (!mountedRef.current) return;
      
      // Set loading state
      setLoading(true);
      
      // Try to refresh teams with multiple attempts
      for (let attempt = 1; attempt <= 3; attempt++) {
        if (!mountedRef.current) return;
        
        try {
          const teams = await getUserTeams();
          
          if (!mountedRef.current) return;
          
          setUserTeams(teams);
          
          // Update current team if needed
          if (teams.length > 0) {
            const savedTeamId = localStorage.getItem('currentTeamId');
            let teamToSet = null;
            
            if (savedTeamId) {
              teamToSet = teams.find(team => team.id === savedTeamId);
            }
            
            if (!teamToSet) {
              teamToSet = getPersonalTeam(teams) || teams[0];
            }
            
            if (teamToSet) {
              setCurrentTeam(teamToSet);
              localStorage.setItem('currentTeamId', teamToSet.id);
            }
          }
          
          setError(null);
          break; // Success, exit the retry loop
          
        } catch (attemptError) {
          if (!mountedRef.current) return;
          
          if (attempt === 3) {
            // Last attempt failed
            setError('Failed to refresh teams after accepting invitation');
            console.error('All refresh attempts failed:', attemptError);
          } else {
            // Wait before next attempt
            await new Promise(resolve => setTimeout(resolve, attempt * 1000));
          }
        }
      }
      
    } catch (error) {
      setError('Failed to refresh teams');
      console.error('Critical error in refreshTeams:', error);
    } finally {
      setLoading(false);
    }
  };

  const value: TeamContextType = {
    currentTeam,
    userTeams,
    switchTeam,
    refreshTeams,
    loading,
    error,
  };

  return (
    <TeamContext.Provider value={value}>
      {children}
    </TeamContext.Provider>
  );
}

export function useTeam() {
  const context = useContext(TeamContext);
  if (context === undefined) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
}